import { useEffect, useRef } from 'react';
import { Message, toaster } from 'rsuite';
import './FlashFill.css';

/*
 * Excel-style flash fill for month-by-month hour grids (activity forecast, staffing scenarios).
 *
 * Two consecutive months with the same non-zero hours suggest that value for the run of empty
 * months right after them. A lightning button sits in the first month of each run: hovering it
 * previews the run, clicking fills it. Every fill raises a toast with Undo, and Ctrl+Z (outside
 * text fields) undoes the most recent fill on the page.
 */

const nextMonthStart = (ms) => {
  const [y, m] = String(ms || '').slice(0, 7).split('-').map(Number);
  if (!Number.isInteger(y) || !Number.isInteger(m)) return null;
  return m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
};

// Returns Map<firstFillMonthStart, { value, months }>; locked months are skipped, never filled.
export const getFlashFillRuns = (monthStarts, getHours, isLocked = () => false) => {
  const out = new Map();
  const months = Array.isArray(monthStarts) ? monthStarts : [];
  const isNextMonth = (a, b) => nextMonthStart(a) === b;

  for (let i = 1; i < months.length; i += 1) {
    const value = Number(getHours(months[i]) ?? 0);
    if (!(value > 0)) continue;
    if (!isNextMonth(months[i - 1], months[i])) continue;
    if (Number(getHours(months[i - 1]) ?? 0) !== value) continue;

    const fillMonths = [];
    for (let j = i + 1; j < months.length; j += 1) {
      if (!isNextMonth(months[j - 1], months[j])) break;
      if (Number(getHours(months[j]) ?? 0) !== 0) break;
      if (!isLocked(months[j])) fillMonths.push(months[j]);
    }
    if (fillMonths.length > 0) out.set(fillMonths[0], { value, months: fillMonths });
  }

  return out;
};

// Per user: Map<monthStart, run> covering every month a run would fill (run.months[0] hosts the button).
export const buildFlashFillCellRuns = (rows, monthStarts, getHours, isLocked) => {
  const out = new Map();
  for (const row of rows) {
    const runs = getFlashFillRuns(monthStarts, (ms) => getHours(row, ms), isLocked);
    if (runs.size === 0) continue;
    const byMonth = new Map();
    for (const run of runs.values()) {
      for (const ms of run.months) byMonth.set(ms, run);
    }
    out.set(row.userId, byMonth);
  }
  return out;
};

export const getFlashFillRunKey = (userId, run) => `${userId}|${run.months[0]}`;

// Unique runs for one user's Map<monthStart, run>, e.g. for Ctrl+E (fill the whole row).
export const uniqueFlashFillRuns = (runsByMonth) => Array.from(new Set(runsByMonth?.values() ?? []));

export const describeFlashFill = (name, runs) => {
  const count = runs.reduce((sum, run) => sum + run.months.length, 0);
  const monthsLabel = `${count} month${count === 1 ? '' : 's'}`;
  const allSame = runs.every((run) => run.value === runs[0].value);
  const who = name ? ` for ${name}` : '';
  return allSame ? `Flash filled ${monthsLabel} with ${runs[0].value}h${who}.` : `Flash filled ${monthsLabel}${who}.`;
};

// Hover previews the run by toggling a class on its cells directly (no React render), so it shows
// instantly. Cells opt in with data-flash-fill-run={runKey} and pre-render a hidden
// <FlashFillGhost> (plus optional .ppc-flash-fill-preview-only / .ppc-flash-fill-preview-hide parts).
export function FlashFillButton({ runKey, run, disabled, onAccept }) {
  const count = run.months.length;
  const label = `Flash fill ${count} month${count === 1 ? '' : 's'} with ${run.value}h`;

  const setPreview = (el, on) => {
    const scope = el?.closest?.('.rs-table, table') || document;
    scope.querySelectorAll(`[data-flash-fill-run="${runKey}"]`).forEach((cell) => {
      cell.classList.toggle('is-flash-fill-preview', on);
    });
  };

  return (
    <button
      type="button"
      className="ppc-flash-fill ppc-noprint"
      title={`${label} (click to accept)`}
      aria-label={label}
      disabled={disabled}
      onMouseEnter={(e) => setPreview(e.currentTarget, true)}
      onMouseLeave={(e) => setPreview(e.currentTarget, false)}
      onFocus={(e) => setPreview(e.currentTarget, true)}
      onBlur={(e) => setPreview(e.currentTarget, false)}
      onClick={(e) => {
        setPreview(e.currentTarget, false);
        onAccept();
      }}
    >
      <i className="bi bi-lightning-charge-fill" aria-hidden="true" />
    </button>
  );
}

// Wraps an input with the hidden ghost value (formatted the way the input shows hours) for previews.
export function FlashFillGhost({ text, children }) {
  return (
    <span className="ppc-flash-fill-input">
      {children}
      <span className="ppc-flash-fill-ghost" aria-hidden="true">
        {text}
      </span>
    </span>
  );
}

// Page-wide order of fills across grids (the forecast's View All has one per activity), so Ctrl+Z
// undoes the latest one wherever it happened.
let flashFillUndoOwners = [];

const isEditableTarget = (el) =>
  Boolean(el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)));

// Undo bookkeeping for a grid: recordFlashFill({ message, undo }) raises the toast and remembers how
// to revert; clearFlashFillUndo() forgets everything (call it when drafts are saved or discarded).
export function useFlashFillUndo({ disabled = false } = {}) {
  const ownerRef = useRef({});
  const undoStackRef = useRef([]); // [{ undo, toastKey }]
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const removeToast = (entry) => {
    Promise.resolve(entry.toastKey).then((key) => {
      if (key) toaster.remove(key);
    });
  };

  const undoEntry = (entry) => {
    if (disabledRef.current) return;
    const idx = undoStackRef.current.indexOf(entry);
    if (idx === -1) return;
    undoStackRef.current.splice(idx, 1);

    const ownerIdx = flashFillUndoOwners.lastIndexOf(ownerRef.current);
    if (ownerIdx !== -1) flashFillUndoOwners.splice(ownerIdx, 1);

    entry.undo();
    removeToast(entry);
  };

  const recordFlashFill = ({ message, undo }) => {
    const entry = { undo, toastKey: null };
    undoStackRef.current.push(entry);
    flashFillUndoOwners.push(ownerRef.current);

    entry.toastKey = toaster.push(
      <Message showIcon type="success" closable>
        <div className="ppc-flash-fill-toast">
          <span>{message}</span>
          <button
            type="button"
            className="ppc-actuals-btn ppc-forecast-btn--sm"
            title="Undo flash fill (Ctrl+Z)"
            onClick={() => undoEntry(entry)}
          >
            <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
            Undo
          </button>
        </div>
      </Message>,
      { placement: 'topEnd', duration: 8000 }
    );
  };

  const clearFlashFillUndo = () => {
    for (const entry of undoStackRef.current) removeToast(entry);
    undoStackRef.current = [];
    flashFillUndoOwners = flashFillUndoOwners.filter((o) => o !== ownerRef.current);
  };

  const undoLatestRef = useRef(null);
  undoLatestRef.current = () => {
    const entry = undoStackRef.current[undoStackRef.current.length - 1];
    if (entry) undoEntry(entry);
  };

  // Ctrl+Z outside text fields undoes the most recent flash fill on the page.
  useEffect(() => {
    const owner = ownerRef.current;
    const onKeyDown = (e) => {
      if (e.defaultPrevented) return;
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.key.toLowerCase() !== 'z') return;
      if (isEditableTarget(e.target)) return;
      if (flashFillUndoOwners[flashFillUndoOwners.length - 1] !== owner) return;
      e.preventDefault();
      undoLatestRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      flashFillUndoOwners = flashFillUndoOwners.filter((o) => o !== owner);
    };
  }, []);

  return { recordFlashFill, clearFlashFillUndo };
}

export const isFlashFillShortcut = (e) => (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'e';
